import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Clock,
  Users,
  Activity,
  Lock
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORT_CHANNELS = ['chat', 'phone', 'email', 'technical'];

const ShiftTemplates = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Check if user is manager or admin
  const isManagerOrAdmin = user && (user.role === 'manager' || user.role === 'admin');
  const [formData, setFormData] = useState({
    name: '',
    shift_type: 'regular',
    start_time: '09:00',
    end_time: '17:00',
    duration_hours: 8,
    required_channels: [],
    min_employees: 1,
    max_employees: 5
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Calculate duration when start or end time changes
    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}`);
      const end = new Date(`2000-01-01T${formData.end_time}`);
      let duration = (end - start) / (1000 * 60 * 60); // Convert to hours
      
      // Handle overnight shifts
      if (duration < 0) {
        duration += 24;
      }
      
      setFormData(prev => ({ ...prev, duration_hours: duration }));
    }
  }, [formData.start_time, formData.end_time]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/shift-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load shift templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTemplate) {
        await axios.put(`${API}/shift-templates/${editingTemplate.id}`, formData);
        toast.success('Shift template updated successfully');
      } else {
        await axios.post(`${API}/shift-templates`, formData);
        toast.success('Shift template created successfully');
      }
      
      fetchTemplates();
      resetForm();
      setShowModal(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save shift template';
      toast.error(message);
    }
  };

  const handleDelete = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this shift template?')) {
      try {
        await axios.delete(`${API}/shift-templates/${templateId}`);
        toast.success('Shift template deleted successfully');
        fetchTemplates();
      } catch (error) {
        toast.error('Failed to delete shift template');
      }
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      shift_type: template.shift_type,
      start_time: template.start_time,
      end_time: template.end_time,
      duration_hours: template.duration_hours,
      required_channels: template.required_channels || [],
      min_employees: template.min_employees,
      max_employees: template.max_employees
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      shift_type: 'regular',
      start_time: '09:00',
      end_time: '17:00',
      duration_hours: 8,
      required_channels: [],
      min_employees: 1,
      max_employees: 5
    });
  };

  const handleChannelToggle = (channel) => {
    const newChannels = formData.required_channels.includes(channel)
      ? formData.required_channels.filter(c => c !== channel)
      : [...formData.required_channels, channel];
    setFormData({ ...formData, required_channels: newChannels });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shift-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shift Templates</h1>
          <p className="text-gray-600 mt-1">
            {isManagerOrAdmin 
              ? 'Create and manage reusable shift templates for scheduling.'
              : 'View available shift templates for scheduling.'}
          </p>
          {!isManagerOrAdmin && (
            <div className="mt-2 flex items-center space-x-2 text-sm text-amber-600">
              <Lock className="h-4 w-4" />
              <span>Read-only: Only managers and admins can create or edit templates</span>
            </div>
          )}
        </div>
        {isManagerOrAdmin && (
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm} 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-template-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Template
              </Button>
            </DialogTrigger>
          
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Shift Template' : 'Add New Shift Template'}
              </DialogTitle>
              <DialogDescription>
                Create a reusable template for scheduling shifts.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  data-testid="template-name-input"
                  type="text"
                  placeholder="e.g., Morning Support, Night Watch"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shift_type">Shift Type *</Label>
                <Select 
                  value={formData.shift_type} 
                  onValueChange={(value) => setFormData({...formData, shift_type: value})}
                >
                  <SelectTrigger data-testid="template-type-select">
                    <SelectValue placeholder="Select shift type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular Shift</SelectItem>
                    <SelectItem value="on_call">On-Call Shift</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time">Start Time *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                    data-testid="template-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time">End Time *</Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                    required
                    data-testid="template-end-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (hrs)</Label>
                  <Input
                    id="duration"
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="24"
                    value={formData.duration_hours}
                    onChange={(e) => setFormData({...formData, duration_hours: parseFloat(e.target.value)})}
                    data-testid="template-duration"
                    readOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_employees">Min Employees *</Label>
                  <Input
                    id="min_employees"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.min_employees}
                    onChange={(e) => setFormData({...formData, min_employees: parseInt(e.target.value)})}
                    required
                    data-testid="template-min-employees"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_employees">Max Employees *</Label>
                  <Input
                    id="max_employees"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.max_employees}
                    onChange={(e) => setFormData({...formData, max_employees: parseInt(e.target.value)})}
                    required
                    data-testid="template-max-employees"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Required Support Channels</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SUPPORT_CHANNELS.map(channel => (
                    <div key={channel} className="flex items-center space-x-2">
                      <Checkbox
                        id={`template-channel-${channel}`}
                        checked={formData.required_channels.includes(channel)}
                        onCheckedChange={() => handleChannelToggle(channel)}
                        data-testid={`template-channel-${channel}`}
                      />
                      <Label htmlFor={`template-channel-${channel}`} className="capitalize">
                        {channel}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  data-testid="cancel-template-button"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  data-testid="save-template-button"
                >
                  {editingTemplate ? 'Update' : 'Create'} Template
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    template.shift_type === 'on_call' ? 'bg-orange-100' : 'bg-blue-100'
                  }`}>
                    <Clock className={`h-6 w-6 ${
                      template.shift_type === 'on_call' ? 'text-orange-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg" data-testid={`template-name-${template.id}`}>
                      {template.name}
                    </CardTitle>
                    <CardDescription className="capitalize">
                      {template.shift_type.replace('_', ' ')} Shift
                    </CardDescription>
                  </div>
                </div>
                <div className="flex space-x-1">
                  {isManagerOrAdmin && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>
                  {formatTime(template.start_time)} - {formatTime(template.end_time)}
                </span>
                <Badge variant="outline" className="ml-2">
                  {template.duration_hours}h
                </Badge>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Users className="h-4 w-4" />
                <span>
                  {template.min_employees} - {template.max_employees} employees
                </span>
              </div>

              {template.required_channels && template.required_channels.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Activity className="h-4 w-4 mr-1" />
                    Required Channels
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {template.required_channels.map((channel, index) => (
                      <Badge key={index} variant="secondary" className="text-xs capitalize">
                        {channel}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {templates.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Clock className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shift templates found</h3>
            <p className="text-gray-600 mb-4">
              Create your first shift template to get started with scheduling.
            </p>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Template
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ShiftTemplates;